/**
 * Click-to-accept gate and the Settings acceptance log.
 * api is the admin API.json client: api(method, path, body) returns parsed JSON and throws on error.
 * LegalAcceptanceGate props: { api, toast, user, onStatus }
 * LegalAcceptanceLog props: { api, user, timezone }
 */
import { formatDateTime } from './datetime.js';

const { useEffect, useRef, useState } = React;

const REMIND_PREFIX = 'legal-gate-remind:';

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function remindKey(user) {
  const id = user && user.id != null ? String(user.id) : '0';
  return REMIND_PREFIX + id;
}

function reminded(key) {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch (e) {
    return false;
  }
}

function remember(key) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch (e) { /* session hide still applies in component state */ }
}

function callApi(api, method, path, body) {
  if (body === undefined) return api(method, path);
  return api(method, path, body);
}

export function LegalAcceptanceGate({ api, toast, user, onStatus }) {
  const [status, setStatus] = useState(null);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const firstCheck = useRef(null);
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  const titleId = React.useId ? React.useId() : 'legal-gate-title';
  const descId = React.useId ? React.useId() : 'legal-gate-desc';
  const storageKey = remindKey(user);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await callApi(api, 'GET', '/api/legal/status');
        if (cancel) return;
        setStatus(data);
        if (typeof onStatusRef.current === 'function') onStatusRef.current(data);
      } catch (err) {
        if (!cancel && toast) toast(errorMessage(err, 'Could not load the legal terms.'), 'error');
      }
    })();
    return () => { cancel = true; };
  }, [api]);

  const docs = status && Array.isArray(status.docs) ? status.docs : [];
  const needsAccept = !!(status && status.can_accept && docs.some((doc) => !doc.tenant_accepted));
  const showModal = needsAccept && !dismissed && !reminded(storageKey);
  const showBanner = !!(status && !status.can_accept && !status.outbound_ready);
  const allChecked = docs.length > 0 && docs.every((doc) => checked[doc.key] === true);

  useEffect(() => {
    if (showModal && firstCheck.current) firstCheck.current.focus();
  }, [showModal]);

  function trapTab(event) {
    if (event.key !== 'Tab' || typeof document === 'undefined') return;
    const root = event.currentTarget;
    const nodes = root.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled)');
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function remindLater() {
    remember(storageKey);
    setDismissed(true);
  }

  async function accept(event) {
    event.preventDefault();
    if (!allChecked || busy) return;
    setBusy(true);
    try {
      const data = await callApi(api, 'POST', '/api/legal/accept', {
        doc_keys: docs.map((doc) => doc.key),
        agree: true
      });
      setStatus(data);
      if (typeof onStatusRef.current === 'function') onStatusRef.current(data);
      if (toast) toast('Acceptance saved.');
    } catch (err) {
      if (toast) toast(errorMessage(err, 'Could not save your acceptance.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!showBanner && !showModal) return null;

  return (
    <div className="legal-gate">
      {showBanner ? (
        <p className="legal-banner" role="status">AI calling is paused until your account admin accepts the updated terms.</p>
      ) : null}
      {showModal ? (
        <div className="legal-overlay">
          <div
            className="legal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            onKeyDown={trapTab}
          >
            <form onSubmit={accept}>
              <div className="legal-dialog-body">
                <h2 id={titleId} className="legal-title">Accept legal terms</h2>
                <p id={descId} className="legal-lead">AI calling is paused for your workspace until an admin accepts these documents.</p>
                {docs.map((doc, index) => (
                  <div key={doc.key} className="legal-doc">
                    <div className="legal-doc-top">
                      <a href={doc.url} target="_blank" rel="noopener noreferrer">{doc.title}</a>
                      <span className="legal-version">Version {doc.version}</span>
                    </div>
                    <label className="legal-check">
                      <input
                        ref={index === 0 ? firstCheck : undefined}
                        type="checkbox"
                        checked={checked[doc.key] === true}
                        onChange={(event) => {
                          const on = event.target.checked;
                          setChecked((prev) => Object.assign({}, prev, { [doc.key]: on }));
                        }}
                      />
                      <span>{'I have read and agree to the ' + doc.title}</span>
                    </label>
                  </div>
                ))}
                <div className="legal-actions">
                  <button type="button" className="legal-btn" onClick={remindLater} disabled={busy}>Remind me later</button>
                  <button type="submit" className="legal-btn legal-btn--primary" disabled={busy || !allChecked}>Agree and continue</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LegalAcceptanceLog({ api, user, timezone }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const headingId = React.useId ? React.useId() : 'legal-log-title';

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await callApi(api, 'GET', '/api/legal/acceptances');
        if (cancel) return;
        setItems(Array.isArray(data && data.items) ? data.items : []);
      } catch (err) {
        if (cancel) return;
        const denied = err && (err.status === 403 || (err.data && err.data.error === 'forbidden'));
        setError(denied
          ? 'Only a workspace admin can view this log.'
          : errorMessage(err, 'Could not load the acceptance log.'));
        setItems([]);
      }
    })();
    return () => { cancel = true; };
  }, [api, user && user.id]);

  return (
    <section className="legal-log" aria-labelledby={headingId}>
      <h2 id={headingId} className="legal-log-title">Legal acceptances</h2>
      {error ? <p className="legal-error" role="status">{error}</p> : null}
      {items == null ? <p className="legal-empty">Loading acceptances…</p> : null}
      {items && items.length === 0 && !error ? <p className="legal-empty">No acceptances yet.</p> : null}
      {items && items.length > 0 ? (
        <div className="legal-table-wrap">
          <table className="legal-table">
            <caption className="legal-sr">Legal document acceptances</caption>
            <thead>
              <tr>
                <th scope="col">Document</th>
                <th scope="col">Version</th>
                <th scope="col">Accepted by</th>
                <th scope="col">Date and time</th>
                <th scope="col">IP</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td>{row.title || row.doc_key}</td>
                  <td>{row.doc_version}</td>
                  <td>
                    <span className="legal-who">{row.name || row.email || 'Unknown user'}</span>
                    {row.name && row.email ? <span className="legal-who-email">{row.email}</span> : null}
                  </td>
                  <td>{formatDateTime(row.accepted_at, { timeZone: timezone, style: 'full' })}</td>
                  <td>{row.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
